export const UNIFIED_AGENT_FILTER_FIELD = '_related_agentCommission_fullname_reverse';

export const SIDE_AGENT_FILTER_FIELDS = [
    '_related_buyerAgent_fullname_reverse',
    '_related_sellerAgent_fullname_reverse',
];

const ALL_AGENT_FILTER_FIELDS = [UNIFIED_AGENT_FILTER_FIELD, ...SIDE_AGENT_FILTER_FIELDS];

const normalizeAgentValues = (value) => {
    if (value == null || value === '') {
        return [];
    }
    if (Array.isArray(value)) {
        return value.filter((entry) => typeof entry === 'string' && entry !== '');
    }
    if (typeof value === 'string') {
        return [value];
    }
    return [];
};

const buildAgentFilterItem = (field, values) => {
    if (values.length === 0) {
        return null;
    }

    return {
        field,
        operator: values.length === 1 ? 'equals' : 'in',
        value: values.length === 1 ? values[0] : values,
    };
};

/**
 * All'atterraggio: CA/CV nel filterModel → filtro agente unificato.
 */
export const migrateToUnifiedAgentFilter = (filterModel) => {
    const items = filterModel?.items;
    if (!Array.isArray(items)) {
        return null;
    }

    const sideItems = items.filter((item) => SIDE_AGENT_FILTER_FIELDS.includes(item.field));
    if (sideItems.length === 0) {
        return null;
    }

    const unifiedValues = new Set();
    sideItems.forEach((item) => {
        normalizeAgentValues(item.value).forEach((name) => unifiedValues.add(name));
    });

    const unifiedItem = items.find((item) => item.field === UNIFIED_AGENT_FILTER_FIELD);
    if (unifiedItem) {
        normalizeAgentValues(unifiedItem.value).forEach((name) => unifiedValues.add(name));
    }

    const withoutAgentFields = items.filter((item) => !ALL_AGENT_FILTER_FIELDS.includes(item.field));
    const agentItem = buildAgentFilterItem(UNIFIED_AGENT_FILTER_FIELD, [...unifiedValues]);

    return {
        items: agentItem ? [...withoutAgentFields, agentItem] : withoutAgentFields,
    };
};

/**
 * All'atterraggio su pagine CA/CV: filtro agente unificato → lato corretto;
 * rimuove il filtro sul lato opposto.
 */
export const migrateToSideAgentFilter = (filterModel, side) => {
    if (side !== 'buyer' && side !== 'seller') {
        return null;
    }

    const items = filterModel?.items;
    if (!Array.isArray(items)) {
        return null;
    }

    const targetField = side === 'buyer'
        ? '_related_buyerAgent_fullname_reverse'
        : '_related_sellerAgent_fullname_reverse';
    const oppositeField = side === 'buyer'
        ? '_related_sellerAgent_fullname_reverse'
        : '_related_buyerAgent_fullname_reverse';

    const unifiedItem = items.find((item) => item.field === UNIFIED_AGENT_FILTER_FIELD);
    const targetItem = items.find((item) => item.field === targetField);
    const oppositeItem = items.find((item) => item.field === oppositeField);

    if (!unifiedItem && !oppositeItem) {
        return null;
    }

    const targetValues = new Set();
    if (unifiedItem) {
        normalizeAgentValues(unifiedItem.value).forEach((name) => targetValues.add(name));
    }
    if (targetItem) {
        normalizeAgentValues(targetItem.value).forEach((name) => targetValues.add(name));
    }

    const withoutAgentFields = items.filter((item) => !ALL_AGENT_FILTER_FIELDS.includes(item.field));
    const sideItem = buildAgentFilterItem(targetField, [...targetValues]);

    return {
        items: sideItem ? [...withoutAgentFields, sideItem] : withoutAgentFields,
    };
};

/**
 * Per le API: traduce il filtro unificato in CA/CV (OR) o lato singolo per buyer/seller.
 */
export const expandUnifiedAgentForGraph = (filters, agentSideFilter) => {
    const items = filters?.items;
    if (!Array.isArray(items)) {
        return filters;
    }

    const unifiedItem = items.find((item) => item.field === UNIFIED_AGENT_FILTER_FIELD);
    if (!unifiedItem) {
        return filters;
    }

    const agentValues = normalizeAgentValues(unifiedItem.value);
    const withoutAgentFields = items.filter((item) => !ALL_AGENT_FILTER_FIELDS.includes(item.field));

    if (agentSideFilter === 'buyer') {
        const buyerItem = buildAgentFilterItem('_related_buyerAgent_fullname_reverse', agentValues);
        return { items: buyerItem ? [...withoutAgentFields, buyerItem] : withoutAgentFields };
    }

    if (agentSideFilter === 'seller') {
        const sellerItem = buildAgentFilterItem('_related_sellerAgent_fullname_reverse', agentValues);
        return { items: sellerItem ? [...withoutAgentFields, sellerItem] : withoutAgentFields };
    }

    return { items: [...withoutAgentFields, unifiedItem] };
};
